import { CommonModule } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import {
  ErrorHandler,
  NgModule,
  Optional,
  Provider,
  SkipSelf,
  inject,
  provideAppInitializer,
} from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { ApplicationinsightsAngularpluginErrorService } from "@microsoft/applicationinsights-angularplugin-js";
import { MarkdownPipe } from "@modules/shared/markdown.pipe";
import ZoomVideo from "@zoom/videosdk";
import { ProfileModule } from "./../profile/profile.module";
import { SharedModule } from "./../shared/shared.module";
import { authInitializerFactory } from "./auth.initializer";
import {
  AlertComponent,
  LinkActionComponent,
  OldAlertComponent,
  SnackbarComponent,
} from "./components";
import { ActionUserInvitationDialogComponent } from "./components/action-user-invitation-dialog/action-user-invitation-dialog.component";
import { InvitationAlreadyUsedDialogComponent } from "./components/invitation-already-used-dialog/invitation-already-used-dialog.component";
import { InvitationInactiveDialogComponent } from "./components/invitation-inactive-dialog/invitation-inactive-dialog.component";
import { InvitationInvalidDialogComponent } from "./components/invitation-invalid-dialog/invitation-invalid-dialog.component";
import { AuthInterceptor, FeatureFlagErrorInterceptor } from "./interceptors";
import {
  AlertService,
  ConService,
  ConversationService,
  DirectoryService,
  FileUploadService,
  FirebaseAnalyticsService,
  LinkService,
  MemoryService,
  MessageService,
  MiscService,
  PatientService,
  RoutingService,
  SharedService,
  UserAccountService,
} from "./old";
import {
  AVATAR_SERVICE,
  AccountService,
  AnalyticsService,
  ApiService,
  AuthService,
  AvatarService,
  BannerService,
  CONVERSATIONS_SERVICE,
  CompaniesService,
  ContactsService,
  ConversationsService,
  IntegrationService,
  MediaService,
  ScribeService,
  SelectionService,
  SettingsService,
  SnackbarService,
  TeamsService,
  USER_SERVICE,
  UserService,
  UsersService,
  WorkspacesService,
  analyticsServiceInitializerFactory,
} from "./services";
import { ActivityService } from "./services/activity.service";
import { ApplicationInsightsService } from "./services/application-insights.service";

const OLD_SERVICES: Provider[] = [
  AlertService,
  ConService,
  ConversationService,
  DirectoryService,
  MiscService,
  PatientService,
  SharedService,
  UserAccountService,
  LinkService,
  FirebaseAnalyticsService,
  MemoryService,
  MessageService,
  FileUploadService,
  RoutingService,
];

const OLD_COMPONENTS = [OldAlertComponent, LinkActionComponent];

const zoomInitializerFactory = () => {
  return () => ZoomVideo.preloadDependentAssets();
};

@NgModule({
  declarations: [
    SnackbarComponent,
    AlertComponent,
    ...OLD_COMPONENTS,
    InvitationAlreadyUsedDialogComponent,
    InvitationInvalidDialogComponent,
    InvitationInactiveDialogComponent,
    ActionUserInvitationDialogComponent,
  ],
  exports: [],
  imports: [
    CommonModule,
    MatSnackBarModule,
    SharedModule,
    ReactiveFormsModule,
    ProfileModule,
    MarkdownPipe,
  ],
  providers: [
    ApiService,
    AuthService,
    SnackbarService,
    TeamsService,
    UserService,
    UsersService,
    WorkspacesService,
    AvatarService,
    CompaniesService,
    ContactsService,
    AccountService,
    ConversationsService,
    ApplicationInsightsService,
    {
      provide: ErrorHandler,
      useClass: ApplicationinsightsAngularpluginErrorService,
    },
    MediaService,
    IntegrationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FeatureFlagErrorInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = authInitializerFactory(inject(AuthService));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = zoomInitializerFactory();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = analyticsServiceInitializerFactory(
        inject(AnalyticsService)
      );
      return initializerFn();
    }),
    {
      provide: USER_SERVICE,
      useClass: UserService,
    },
    {
      provide: CONVERSATIONS_SERVICE,
      useClass: ConversationsService,
    },
    {
      provide: AVATAR_SERVICE,
      useClass: AvatarService,
    },
    SettingsService,
    ActivityService,
    SelectionService,
    AnalyticsService,
    BannerService,
    ScribeService,
    ...OLD_SERVICES,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class CoreModule {
  // See: https://angular.io/guide/singleton-services#prevent-reimport-of-the-greetingmodule
  public constructor(@Optional() @SkipSelf() parentModule?: CoreModule) {
    if (parentModule) {
      throw new Error(
        "CoreModule is already loaded. Import it in the AppModule only"
      );
    }
  }
}
